import React, { useMemo } from 'react';
import { Box, Typography, Divider, IconButton, Button } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { getTranslationValue } from '../../../constants/posMenu';
import {
  PRICE_TYPES,
  IN_ORDER_VIEW_LAYOUTS,
  getPathKeyLevel,
  extractParentOptionId,
  getEffectiveInOrderViewLayout,
  distributeMergedFreeCount,
} from './previewHelpers';
import { POS_COLORS } from './posColors';

// Indent constants – matching POS client
const VISUAL_INDENT = 20; // px per nesting level
const BASE_INDENT = 20; // base indent for level-0 groups

const getFloatPrice = (price) => {
  return `$${(price || 0).toFixed(2)}`;
};

// ---------------------------------------------------------------------------
// ModifierGroupItem – direct port from POS client CurrentSelectionOrders.tsx
// and OrderProduct/index.tsx ModifierGroupItem component
// ---------------------------------------------------------------------------
const ModifierGroupItem = ({
  modifier,
  level = 0,
  parentOptionName,
  parentOptionCount,
  parentOptionPrice,
  parentOptionFreeCount,
  parentOptionPriceType,
  parentModifierGroupHideNameOnOrder = false,
  parentModifierGroupCanSelectMultipleOptions = false,
  parentOptionPathKeys = new Set(),
  modifierPathKey = '',
  isFirstWithParent = true,
  effectiveParentOptionName,
  pendingMergeOptionNames = [],
  pendingMergeRightOptionNames = [],
  hideParentBecauseDescendantMerges = false,
  suppressMergedLine = false,
  isGroupedByNested = false,
  groupedNestedOptionName,
  groupedParentOptions,
}) => {
  // Check if this modifier group allows selecting multiple options (from order data)
  const canSelectMultipleOptions = modifier.canSelectMultipleOptions !== false;

  // Check if modifier group name should be hidden
  const hideModifierGroupName = modifier.hideNameOnOrder === true;

  // Use effective parent name if available (for multi-level merge), otherwise use direct parent
  const mergeTargetParentName = effectiveParentOptionName || parentOptionName;

  // Parent canSelectMultipleOptions
  const parentCanSelectMultipleOptions =
    parentModifierGroupCanSelectMultipleOptions;

  // Filter options: remove parents (shown as headers) and hideNameOnOrder options
  // Also calculate freeCount per option from the MG's `free` property
  const modifierGroupDataOptions = useMemo(() => {
    const opts = (
      Array.isArray(modifier.modifierOptions)
        ? modifier.modifierOptions
        : Object.values(modifier.modifierOptions || {})
    ).filter((o) => o !== null && o !== undefined);
    const filtered = opts.filter((opt) => {
      const optionPathKey = `${modifierPathKey}__op_${opt._id}`;
      if (parentOptionPathKeys.has(optionPathKey)) {
        // Keep options that participate in a merge (inOrderViewLayout + multiplierApplyingToParentPrice)
        // even if they have children - they should still appear in the merged line
        const effectiveLayout = getEffectiveInOrderViewLayout(
          { inOrderViewLayout: opt.inOrderViewLayout },
          { inOrderViewLayout: modifier.inOrderViewLayout }
        );
        const wouldMerge =
          opt.priceType === PRICE_TYPES.MULTIPLIER_APPLYING_TO_PARENT_PRICE &&
          !!effectiveLayout;
        if (!wouldMerge) {
          return false;
        }
      }
      if (opt.hideNameOnOrder === true) return false;
      return true;
    });
    // Sort options by menu-defined order (optionIds) so they appear
    // in the item's configured order, not in user selection order.
    const optionIds = modifier.optionIds || [];
    if (optionIds.length > 0) {
      filtered.sort((a, b) => {
        const indexA = optionIds.indexOf(a._id);
        const indexB = optionIds.indexOf(b._id);
        return (
          (indexA === -1 ? 9999 : indexA) - (indexB === -1 ? 9999 : indexB)
        );
      });
    }

    return filtered;
  }, [modifier, parentOptionPathKeys, modifierPathKey]);

  // Check if we should merge parent option name with selected option names
  // Condition: ANY displayed option has multiplierApplyingToParentPrice AND effective inOrderViewLayout has a value
  const shouldMergeParentAndOption = useMemo(() => {
    if (!parentOptionName || level === 0) return false;
    // Check if any displayed option has priceType = multiplierApplyingToParentPrice
    // AND has an effective inOrderViewLayout value
    return modifierGroupDataOptions.some((opt) => {
      const effectiveLayout = getEffectiveInOrderViewLayout(
        { inOrderViewLayout: opt.inOrderViewLayout },
        { inOrderViewLayout: modifier.inOrderViewLayout }
      );
      return (
        opt.priceType === PRICE_TYPES.MULTIPLIER_APPLYING_TO_PARENT_PRICE &&
        !!effectiveLayout
      );
    });
  }, [modifierGroupDataOptions, parentOptionName, level, modifier]);

  const indent = BASE_INDENT + level * VISUAL_INDENT;
  const parentHeaderMargin = -VISUAL_INDENT;
  // For multi-level merge chains, pull back further to align at the effective parent's position
  const mergedLineMargin =
    -(1 + (pendingMergeOptionNames?.length || 0)) * VISUAL_INDENT;

  // Only show parent header if this is a child group AND it's the first group with this parent
  // Hide when merging OR when a descendant will use this parent
  const showParentHeader =
    parentOptionName &&
    level > 0 &&
    isFirstWithParent &&
    !shouldMergeParentAndOption &&
    !hideParentBecauseDescendantMerges;

  // Parent options by definition have nested items, hide price if price is 0
  const shouldHideParentPrice = parentOptionPrice === 0;

  if (isGroupedByNested && groupedNestedOptionName !== undefined) {
    const parentOpts = groupedParentOptions || [];
    const groupName =
      typeof groupedNestedOptionName === 'string'
        ? groupedNestedOptionName
        : groupedNestedOptionName?.en || '';
    return (
      <Box sx={{ ml: `${indent}px` }}>
        <Box
          sx={{ display: 'flex', gap: 1, mb: parentOpts.length > 0 ? 0.5 : 0 }}
        >
          <Typography
            noWrap
            sx={{ fontSize: 13, fontWeight: 600, color: POS_COLORS.BLACK }}
          >
            {groupName}
          </Typography>
        </Box>
        {parentOpts.map((opt, idx) => {
          const optPrice = opt.singlePrice ?? opt.price ?? 0;
          const shouldHideOptPrice = optPrice === 0;
          return (
            <Box
              key={`${opt._id}-${idx}`}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                ml: `${VISUAL_INDENT}px`,
              }}
            >
              <Box sx={{ display: 'flex', gap: 1, flex: 1 }}>
                {parentModifierGroupCanSelectMultipleOptions &&
                (!opt?.isSizeOption || (opt.count ?? 0) > 1) ? (
                  <Typography sx={{ fontSize: 12, color: POS_COLORS.BLACK }}>
                    {`${opt.count ?? 1}x`}
                  </Typography>
                ) : null}
                <Typography
                  noWrap
                  sx={{ fontSize: 12, color: POS_COLORS.BLACK, flex: 1 }}
                >
                  {opt?.name?.en}
                </Typography>
                {opt.freeCount ? (
                  <Typography sx={{ fontSize: 12, color: POS_COLORS.BLACK }}>
                    {`(${opt.freeCount}x free)`}
                  </Typography>
                ) : null}
              </Box>
              {!shouldHideOptPrice && (
                <Typography sx={{ fontSize: 12, color: POS_COLORS.BLACK }}>
                  {`$${Number(optPrice).toFixed(2)}`}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    );
  }

  // Skip rendering entirely when this MG's merged line is suppressed and there's nothing visible.
  // When suppressMergedLine is true, merge-participating options won't be shown,
  // so count only non-merge individual options.
  if (suppressMergedLine && !showParentHeader) {
    const hasIndividualOptions = modifierGroupDataOptions.some((opt) => {
      const effectiveLayout = getEffectiveInOrderViewLayout(
        { inOrderViewLayout: opt.inOrderViewLayout },
        { inOrderViewLayout: modifier.inOrderViewLayout }
      );
      const isMergeOption =
        effectiveLayout &&
        opt.priceType === PRICE_TYPES.MULTIPLIER_APPLYING_TO_PARENT_PRICE;
      return !isMergeOption;
    });
    if (!hasIndividualOptions) {
      return null;
    }
  }

  return (
    <Box key={modifier._id} sx={{ ml: `${indent}px`, mb: 0.25 }}>
      {/* Parent option header – at parent modifier group level */}
      {showParentHeader && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mb: 0.25,
            ml: `${parentHeaderMargin}px`,
          }}
        >
          <Box sx={{ display: 'flex', gap: 1, flex: 1 }}>
            {parentCanSelectMultipleOptions && (
              <Typography variant="caption" sx={{ color: POS_COLORS.BLACK }}>
                {`${parentOptionCount || 1}x`}
              </Typography>
            )}
            <Typography
              noWrap
              variant="caption"
              sx={{ color: POS_COLORS.BLACK, flex: 1 }}
            >
              {parentOptionName}
            </Typography>
            {parentOptionFreeCount ? (
              <Typography variant="caption" sx={{ color: POS_COLORS.BLACK }}>
                {`(${parentOptionFreeCount}x free)`}
              </Typography>
            ) : null}
          </Box>
          {!shouldHideParentPrice && (
            <Typography
              variant="caption"
              sx={{
                color: POS_COLORS.BLACK,
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              {getFloatPrice(parentOptionPrice || 0)}
            </Typography>
          )}
        </Box>
      )}

      {/* Modifier group name – conditionally rendered based on hideNameOnOrder, also hidden when merging or for continuation segments */}
      {!hideModifierGroupName &&
        !shouldMergeParentAndOption &&
        !modifier.isSegmentContinuation && (
          <Typography
            noWrap
            variant="caption"
            sx={{ color: POS_COLORS.BLACK, fontWeight: 700 }}
          >
            {getTranslationValue(modifier?.name)}
          </Typography>
        )}

      {/* Options – render merged line for options with inOrderViewLayout, individual lines for others */}
      {(() => {
        // Separate options into merge participants (have effectiveInOrderViewLayout) and individual options
        const mergeOptions = [];
        const individualOptions = [];
        if (shouldMergeParentAndOption) {
          modifierGroupDataOptions.forEach((opt) => {
            const effectiveLayout = getEffectiveInOrderViewLayout(
              { inOrderViewLayout: opt.inOrderViewLayout },
              { inOrderViewLayout: modifier.inOrderViewLayout }
            );
            // Must match BOTH conditions (same as shouldMergeParentAndOption detection)
            // to avoid non-join options being merged via MG-level layout fallback
            if (
              effectiveLayout &&
              opt.priceType === PRICE_TYPES.MULTIPLIER_APPLYING_TO_PARENT_PRICE
            ) {
              mergeOptions.push(opt);
            } else {
              individualOptions.push(opt);
            }
          });
        } else {
          individualOptions.push(...modifierGroupDataOptions);
        }

        // Build merged display name from left/right options
        const leftOptions = mergeOptions.filter(
          (opt) =>
            getEffectiveInOrderViewLayout(
              { inOrderViewLayout: opt.inOrderViewLayout },
              { inOrderViewLayout: modifier.inOrderViewLayout }
            ) === IN_ORDER_VIEW_LAYOUTS.JOIN_PARENT_FROM_LEFT
        );
        const rightOptions = mergeOptions.filter(
          (opt) =>
            getEffectiveInOrderViewLayout(
              { inOrderViewLayout: opt.inOrderViewLayout },
              { inOrderViewLayout: modifier.inOrderViewLayout }
            ) === IN_ORDER_VIEW_LAYOUTS.JOIN_PARENT_FROM_RIGHT
        );
        const leftPart = [
          ...leftOptions
            .map((o) => getTranslationValue(o.name))
            .filter(Boolean),
          ...[...pendingMergeOptionNames].reverse(),
        ].join(', ');
        const rightPart = [
          ...pendingMergeRightOptionNames,
          ...rightOptions
            .map((o) => getTranslationValue(o.name))
            .filter(Boolean),
        ].join(', ');
        const mergedDisplayName = [leftPart, mergeTargetParentName, rightPart]
          .filter(Boolean)
          .join(' ');

        const mergedFreeCount = distributeMergedFreeCount(
          parentOptionFreeCount || 0,
          mergeOptions
        ).total;

        return (
          <>
            {/* Merged line for options with inOrderViewLayout */}
            {mergeOptions.length > 0 && !suppressMergedLine && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  ml: `${mergedLineMargin}px`,
                }}
              >
                <Box sx={{ display: 'flex', gap: 1, flex: 1 }}>
                  <Typography
                    noWrap
                    variant="caption"
                    sx={{ color: POS_COLORS.BLACK, flex: 1 }}
                  >
                    {mergedDisplayName}
                  </Typography>
                  {mergedFreeCount ? (
                    <Typography
                      variant="caption"
                      sx={{ color: POS_COLORS.BLACK }}
                    >
                      {`(${mergedFreeCount} free)`}
                    </Typography>
                  ) : null}
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: POS_COLORS.BLACK,
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                >
                  {getFloatPrice(
                    mergeOptions.reduce(
                      (sum, opt) => sum + (opt.singlePrice ?? opt.price ?? 0),
                      0
                    )
                  )}
                </Typography>
              </Box>
            )}
            {/* Individual lines for options without inOrderViewLayout */}
            {individualOptions.map((optionItem, index) => {
              // Check if option has nested items and price is 0 → hide price
              const hasNestedItems =
                (optionItem.modifierGroupIds?.length ?? 0) > 0;
              const optPrice = optionItem.singlePrice ?? optionItem.price ?? 0;
              const shouldHideOptionPrice = hasNestedItems && optPrice === 0;

              return (
                <Box
                  key={`${optionItem._id}-${index}`}
                  sx={{ display: 'flex', justifyContent: 'space-between' }}
                >
                  <Box sx={{ display: 'flex', gap: 1, flex: 1 }}>
                    {canSelectMultipleOptions &&
                    (!optionItem?.isSizeOption || optionItem.count > 1) ? (
                      <Typography
                        variant="caption"
                        sx={{ color: POS_COLORS.BLACK }}
                      >
                        {`${optionItem.count}x`}
                      </Typography>
                    ) : null}
                    <Typography
                      noWrap
                      variant="caption"
                      sx={{ color: POS_COLORS.BLACK, flex: 1 }}
                    >
                      {getTranslationValue(optionItem?.name)}
                    </Typography>
                    {optionItem.freeCount ? (
                      <Typography
                        variant="caption"
                        sx={{ color: POS_COLORS.BLACK }}
                      >
                        {`(${optionItem.freeCount || 1}x free)`}
                      </Typography>
                    ) : null}
                  </Box>
                  {!shouldHideOptionPrice && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: POS_COLORS.BLACK,
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                      }}
                    >
                      {getFloatPrice(optPrice)}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </>
        );
      })()}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// buildModifierGroupData – direct port from POS client
// Used by both CurrentSelection and OrderItemRow
// ---------------------------------------------------------------------------
const buildModifierGroupData = ({
  groups, // array of MG objects (with pathKey on each)
  productModifierGroupIds, // product's modifierGroupIds for level-0 sort
}) => {
  if (!groups || groups.length === 0)
    return { data: [], parentOptionPathKeys: new Set() };

  // Build parentOptionPathKeys
  const parentOptionPathKeys = new Set();
  for (const group of groups) {
    const pathKey = group.pathKey || '';
    const lastMgIndex = pathKey.lastIndexOf('__mg_');
    if (lastMgIndex > 0) {
      const parentOptionPathKey = pathKey.substring(0, lastMgIndex);
      if (parentOptionPathKey.includes('__op_')) {
        parentOptionPathKeys.add(parentOptionPathKey);
      }
    }
  }

  // Build optionDataMap and optionToParentMgMap
  const optionDataMap = {};
  const optionToParentMgMap = {};
  const groupByRawPathKey = {};
  for (const group of groups) {
    const gPk = group.pathKey || group._id || '';
    if (gPk) {
      groupByRawPathKey[gPk] = group;
    }
    const opts = Array.isArray(group.modifierOptions)
      ? group.modifierOptions
      : Object.values(group.modifierOptions || {});

    for (const opt of opts) {
      if (opt && opt._id) {
        const optCount = opt.count || 1;
        optionDataMap[opt._id] = {
          count: optCount,
          price: opt.singlePrice ?? opt.price ?? 0,
          name: opt.name?.en || getTranslationValue(opt.name) || '',
          priceType: opt.priceType || null,
          inOrderViewLayout: opt.inOrderViewLayout || null,
          freeCount: opt.freeCount || 0,
          modifierGroupIds: opt.modifierGroupIds || null,
        };
        optionToParentMgMap[opt._id] = group._id || '';
      }
    }
  }

  // Build groupsByPathKey map
  const groupsByPathKey = new Map();
  for (let groupIndex = 0; groupIndex < groups.length; groupIndex++) {
    const group = groups[groupIndex];
    let pathKey = group.pathKey || group._id || '';
    if (!pathKey) {
      pathKey = `fallback_mg_${groupIndex}_${
        getTranslationValue(group.name) || 'unnamed'
      }`;
    }
    const level = getPathKeyLevel(pathKey);

    // Extract parent option info
    let parentOptionName;
    let parentOptionCount;
    let parentOptionPrice;
    let parentOptionFreeCount;
    let parentOptionPriceType;
    let parentModifierGroupHideNameOnOrder;
    let parentModifierGroupCanSelectMultipleOptions;

    if (level > 0 && pathKey) {
      const parentOptionId = extractParentOptionId(pathKey);
      if (parentOptionId) {
        const parentOptionData = optionDataMap[parentOptionId];
        if (parentOptionData) {
          parentOptionName = parentOptionData.name;
          parentOptionCount = parentOptionData.count;
          parentOptionPrice = parentOptionData.price;
          parentOptionFreeCount = parentOptionData.freeCount;
          parentOptionPriceType = parentOptionData.priceType;
        }
        // Override freeCount from the correct MG instance via pathKey
        // (optionDataMap is keyed by opt._id which collides when the same
        // option appears in multiple instances, e.g. left/right pizza halves)
        const lastOpIdx = pathKey.lastIndexOf('__op_');
        if (lastOpIdx > 0) {
          const parentMgPk = pathKey.substring(0, lastOpIdx);
          const parentMgInst = groupByRawPathKey[parentMgPk];
          if (parentMgInst) {
            const mgOpts = Array.isArray(parentMgInst.modifierOptions)
              ? parentMgInst.modifierOptions
              : Object.values(parentMgInst.modifierOptions || {});
            const parentOpt = mgOpts.find((o) => o?._id === parentOptionId);
            if (parentOpt) {
              parentOptionFreeCount = parentOpt.freeCount || 0;
            }
          }
        }
        // Get the parent modifier group's hideNameOnOrder and canSelectMultipleOptions
        const parentMgId = optionToParentMgMap[parentOptionId];
        if (parentMgId) {
          const parentMg = groups.find((mg) => mg._id === parentMgId);
          parentModifierGroupHideNameOnOrder =
            parentMg?.hideNameOnOrder === true;
          parentModifierGroupCanSelectMultipleOptions =
            parentMg?.canSelectMultipleOptions === true;
        }
      }
    }

    groupsByPathKey.set(pathKey, {
      ...group,
      level,
      pathKey,
      parentOptionName,
      parentOptionCount,
      parentOptionPrice,
      parentOptionFreeCount,
      parentOptionPriceType,
      parentModifierGroupHideNameOnOrder,
      parentModifierGroupCanSelectMultipleOptions,
    });
  }

  // Second pass: multi-level merge – direct port from POS client
  try {
    for (const [pathKey, mg] of groupsByPathKey.entries()) {
      if (mg.level === 0) continue;

      // Check if THIS MG would merge (has options with multiplier priceType AND effective inOrderViewLayout)
      const thisMgOptions = Array.isArray(mg.modifierOptions)
        ? mg.modifierOptions
        : Object.values(mg.modifierOptions || {});
      const thisMgWouldMerge = thisMgOptions.some((opt) => {
        const effectiveLayout = getEffectiveInOrderViewLayout(
          { inOrderViewLayout: opt?.inOrderViewLayout },
          { inOrderViewLayout: mg?.inOrderViewLayout }
        );
        return (
          opt?.priceType === PRICE_TYPES.MULTIPLIER_APPLYING_TO_PARENT_PRICE &&
          !!effectiveLayout
        );
      });

      if (!thisMgWouldMerge) continue;

      // Walk up the parent chain to find the "root" parent that wasn't merged
      let effectiveParent = mg.parentOptionName;
      const pendingNames = [];
      const pendingRightNames = [];
      let currentPathKey = pathKey;
      let iterations = 0;

      while (iterations < 10) {
        iterations++;
        const parentOptionId = extractParentOptionId(currentPathKey);
        if (!parentOptionId) break;

        const parentMgId = optionToParentMgMap[parentOptionId];
        if (!parentMgId) break;

        // Find parent MG in groupsByPathKey
        let parentMg;
        for (const [pk, m] of groupsByPathKey.entries()) {
          if (m._id === parentMgId && currentPathKey.startsWith(pk + '__')) {
            parentMg = m;
            break;
          }
        }
        if (!parentMg) break;

        // Check if parent MG would also merge
        const parentOptData = optionDataMap[parentOptionId];
        const parentOptPriceType = parentOptData?.priceType;
        const parentEffectiveLayout = getEffectiveInOrderViewLayout(
          { inOrderViewLayout: parentOptData?.inOrderViewLayout },
          { inOrderViewLayout: parentMg?.inOrderViewLayout }
        );
        const parentWouldMerge =
          !!parentEffectiveLayout &&
          parentOptPriceType ===
            PRICE_TYPES.MULTIPLIER_APPLYING_TO_PARENT_PRICE;

        if (parentWouldMerge) {
          // Parent would merge, add its name to pending left or right based on layout
          if (parentOptData?.name) {
            if (
              parentEffectiveLayout ===
              IN_ORDER_VIEW_LAYOUTS.JOIN_PARENT_FROM_RIGHT
            ) {
              pendingRightNames.unshift(parentOptData.name);
            } else {
              pendingNames.unshift(parentOptData.name);
            }
          }
          // Include sibling merge options without descendants in the same line
          const parentMgOpts = Array.isArray(parentMg.modifierOptions)
            ? parentMg.modifierOptions
            : Object.values(parentMg.modifierOptions || {});
          for (const sibOpt of parentMgOpts) {
            if (!sibOpt || sibOpt._id === parentOptionId) continue;
            const sibLayout = getEffectiveInOrderViewLayout(
              { inOrderViewLayout: sibOpt.inOrderViewLayout },
              { inOrderViewLayout: parentMg.inOrderViewLayout }
            );
            const sibMerges =
              sibOpt.priceType ===
                PRICE_TYPES.MULTIPLIER_APPLYING_TO_PARENT_PRICE && !!sibLayout;
            if (!sibMerges || !sibOpt.name) continue;
            const sibName =
              typeof sibOpt.name === 'string'
                ? sibOpt.name
                : sibOpt.name?.en || getTranslationValue(sibOpt.name);
            if (!sibName) continue;
            // Only include if this sibling has NO descendant MGs
            const sibOptPk = `${parentMg.pathKey}__op_${sibOpt._id}`;
            const sibHasDescendants = [...groupsByPathKey.keys()].some((pk) =>
              pk.startsWith(sibOptPk + '__mg_')
            );
            if (sibHasDescendants) continue;
            if (sibLayout === IN_ORDER_VIEW_LAYOUTS.JOIN_PARENT_FROM_RIGHT) {
              pendingRightNames.push(sibName);
            } else {
              pendingNames.push(sibName);
            }
          }
          effectiveParent = parentMg.parentOptionName;
          currentPathKey = parentMg.pathKey;
        } else {
          break;
        }
      }

      const updated = groupsByPathKey.get(pathKey);
      if (updated) {
        updated.effectiveParentOptionName = effectiveParent;
        updated.pendingMergeOptionNames = pendingNames;
        updated.pendingMergeRightOptionNames = pendingRightNames;
        groupsByPathKey.set(pathKey, updated);

        // Mark ancestor MGs whose parentOptionName equals effectiveParent to hide their parent header
        if (effectiveParent) {
          for (const [ancestorPk, ancestorMg] of groupsByPathKey.entries()) {
            if (
              pathKey.startsWith(ancestorPk + '__') &&
              ancestorMg.parentOptionName === effectiveParent
            ) {
              ancestorMg.hideParentBecauseDescendantMerges = true;
              ancestorMg.suppressMergedLine = true;
              groupsByPathKey.set(ancestorPk, ancestorMg);
            }
          }
        }
      }
    }
  } catch (err) {
    // Silent catch for second pass errors
  }

  // Third pass: when a merge-participating option has children, hide parent header on its child MGs
  // so the option name isn't shown twice (once in merged line, once as parent header)
  try {
    for (const [pathKey, mg] of groupsByPathKey.entries()) {
      const mgOptions = Array.isArray(mg.modifierOptions)
        ? mg.modifierOptions
        : Object.values(mg.modifierOptions || {});
      const mgWouldMerge =
        mg.parentOptionName &&
        mg.level > 0 &&
        mgOptions.some((opt) => {
          if (!opt) return false;
          const effectiveLayout = getEffectiveInOrderViewLayout(
            { inOrderViewLayout: opt.inOrderViewLayout },
            { inOrderViewLayout: mg.inOrderViewLayout }
          );
          return (
            opt.priceType === PRICE_TYPES.MULTIPLIER_APPLYING_TO_PARENT_PRICE &&
            !!effectiveLayout
          );
        });
      if (!mgWouldMerge) continue;
      // For each merge-participating option, check if it has child MGs
      for (const opt of mgOptions) {
        if (!opt) continue;
        const effectiveLayout = getEffectiveInOrderViewLayout(
          { inOrderViewLayout: opt.inOrderViewLayout },
          { inOrderViewLayout: mg.inOrderViewLayout }
        );
        const optMerges =
          opt.priceType === PRICE_TYPES.MULTIPLIER_APPLYING_TO_PARENT_PRICE &&
          !!effectiveLayout;
        if (!optMerges) continue;
        const optPathKey = `${pathKey}__op_${opt._id || ''}`;
        // Mark direct child MGs to hide their parent header
        for (const [childPk, childMg] of groupsByPathKey.entries()) {
          if (childPk.startsWith(optPathKey + '__mg_')) {
            childMg.hideParentBecauseDescendantMerges = true;
            groupsByPathKey.set(childPk, childMg);
          }
        }
      }
    }
  } catch (err) {
    // Silent catch for third pass errors
  }

  // Recursive ordering
  const findNestedMGs = (optionPathPrefix) => {
    const nested = [];
    for (const [pk, mg] of groupsByPathKey.entries()) {
      if (
        pk.startsWith(optionPathPrefix + '__mg_') &&
        pk.split('__').length === optionPathPrefix.split('__').length + 1
      ) {
        nested.push(mg);
      }
    }
    const parentOptionId = extractParentOptionId(optionPathPrefix);
    const parentModifierGroupIds = parentOptionId
      ? optionDataMap[parentOptionId]?.modifierGroupIds || []
      : [];
    return nested.sort((a, b) => {
      const indexA = parentModifierGroupIds.indexOf(a._id || '');
      const indexB = parentModifierGroupIds.indexOf(b._id || '');
      return (indexA === -1 ? 9999 : indexA) - (indexB === -1 ? 9999 : indexB);
    });
  };

  // Recursive function to add MGs in correct order.
  // Splits a MG into segments around parent options so that nested MG entries
  // appear at the correct sorted position (not always at the bottom).
  const result = [];
  const processed = new Set();

  const addMGRecursively = (mg) => {
    if (processed.has(mg.pathKey)) return;
    processed.add(mg.pathKey);

    // Get options sorted by menu order (optionIds)
    const optionIds = mg.optionIds || [];
    const allOptions = (
      Array.isArray(mg.modifierOptions)
        ? mg.modifierOptions
        : Object.values(mg.modifierOptions || {})
    ).filter((o) => o !== null && o !== undefined);
    if (optionIds.length > 0) {
      allOptions.sort((a, b) => {
        const idxA = optionIds.indexOf(a._id);
        const idxB = optionIds.indexOf(b._id);
        return (idxA === -1 ? 9999 : idxA) - (idxB === -1 ? 9999 : idxB);
      });
    }

    // If a nested MG has sortParentOptionsByThisOptionsSorting, re-sort parent
    // options by the first selected nested option in that MG's optionIds order.
    const getFirstSelectedNestedOptionIndex = (opt) => {
      const optionPathKey = `${mg.pathKey}__op_${opt._id}`;
      const nestedList = findNestedMGs(optionPathKey);
      const nestedWithSort = nestedList.find(
        (n) => n.sortParentOptionsByThisOptionsSorting === true
      );
      if (!nestedWithSort) return 9999;
      const nestedOptIds = nestedWithSort.optionIds || [];
      if (nestedOptIds.length === 0) return 9999;
      const nestedOpts = Array.isArray(nestedWithSort.modifierOptions)
        ? nestedWithSort.modifierOptions
        : Object.values(nestedWithSort.modifierOptions || {});
      const selected = nestedOpts.filter((o) => o && (o.count ?? 0) > 0);
      let minIdx = 9999;
      for (const o of selected) {
        const idx = nestedOptIds.indexOf(o._id);
        if (idx !== -1 && idx < minIdx) minIdx = idx;
      }
      return minIdx;
    };
    const hasSortParent = allOptions.some((opt) => {
      const optionPathKey = `${mg.pathKey}__op_${opt._id}`;
      return findNestedMGs(optionPathKey).some(
        (n) => n.sortParentOptionsByThisOptionsSorting === true
      );
    });
    if (hasSortParent) {
      allOptions.sort((a, b) => {
        const idxA = getFirstSelectedNestedOptionIndex(a);
        const idxB = getFirstSelectedNestedOptionIndex(b);
        return idxA - idxB;
      });
    }

    // Identify parent options (those with nested MGs) and their positions.
    // Options that participate in merge (join parent) stay in segments;
    // only non-merge parent options become split points.
    const splitEntries = [];
    const mergeParentNestedMGs = [];
    for (let i = 0; i < allOptions.length; i++) {
      const opt = allOptions[i];
      const optionPathKey = `${mg.pathKey}__op_${opt._id}`;
      const nested = findNestedMGs(optionPathKey);
      if (nested.length > 0) {
        // Check if this option participates in merge (join parent)
        const effectiveLayout = getEffectiveInOrderViewLayout(
          { inOrderViewLayout: opt.inOrderViewLayout },
          { inOrderViewLayout: mg.inOrderViewLayout }
        );
        const wouldMerge =
          opt.priceType === PRICE_TYPES.MULTIPLIER_APPLYING_TO_PARENT_PRICE &&
          !!effectiveLayout;

        if (wouldMerge) {
          // Keep in segment - don't split. Push nested MGs after segment.
          mergeParentNestedMGs.push(nested);
        } else {
          // Split point - remove from segment, show as parent header
          splitEntries.push({ index: i, nestedMGs: nested });
        }
      }
    }

    // If any nested MG has groupAllParentsByThisOption, emit grouped view
    const allNestedForGroupCheck = [
      ...splitEntries.flatMap((e) => e.nestedMGs),
      ...mergeParentNestedMGs.flat(),
    ];
    const nestedMGWithGroupBy = allNestedForGroupCheck.find(
      (n) => n.groupAllParentsByThisOption === true
    );
    if (nestedMGWithGroupBy) {
      const nestedMgId = nestedMGWithGroupBy._id || '';
      const nestedOptIdToParentOptions = {};
      const discoveredNestedOptIds = [];
      for (const opt of allOptions) {
        const optionPathKey = `${mg.pathKey}__op_${opt._id}`;
        const nestedGroupPathKey = `${optionPathKey}__mg_${nestedMgId}`;
        const nestedInstance = groupsByPathKey.get(nestedGroupPathKey);
        if (nestedInstance?.modifierOptions) {
          const nestedModOpts = Array.isArray(nestedInstance.modifierOptions)
            ? nestedInstance.modifierOptions
            : Object.values(nestedInstance.modifierOptions || {});
          const selectedIds = nestedModOpts
            .filter((o) => o != null && (o.count ?? 0) > 0)
            .map((o) => o._id);
          for (const selId of selectedIds) {
            if (selId) {
              if (!nestedOptIdToParentOptions[selId]) {
                nestedOptIdToParentOptions[selId] = [];
                discoveredNestedOptIds.push(selId);
              }
              nestedOptIdToParentOptions[selId].push(opt);
            }
          }
        }
      }
      const menuOptionIds = nestedMGWithGroupBy.optionIds || [];
      const orderedNestedOptIds =
        menuOptionIds.length > 0
          ? menuOptionIds.filter((id) => nestedOptIdToParentOptions[id])
          : discoveredNestedOptIds;
      result.push({ ...mg, modifierOptions: {} });
      const _groupedEntries = [];
      for (const nestedOptId of orderedNestedOptIds) {
        if (!optionDataMap[nestedOptId]) continue;
        const name = optionDataMap[nestedOptId].name;
        const parentOpts = nestedOptIdToParentOptions[nestedOptId] || [];
        const enhancedParentOpts = parentOpts.map((opt) => {
          const basePath = `${mg.pathKey}__op_${opt._id}__mg_${nestedMgId}__op_${nestedOptId}`;
          const subNames = [];
          let subPrice = 0;
          let subAQ = 0;
          for (const [pk, g] of groupsByPathKey) {
            if (pk.startsWith(basePath + '__mg_')) {
              const subOpts = Array.isArray(g.modifierOptions)
                ? g.modifierOptions
                : Object.values(g.modifierOptions || {});
              for (const subOpt of subOpts) {
                if (subOpt && (subOpt.count ?? 0) > 0) {
                  if (subOpt.name?.en) subNames.push(subOpt.name.en);
                  subPrice += subOpt.singlePrice ?? subOpt.price ?? 0;
                  subAQ += subOpt.actualQuantityWithMultiplier || 0;
                }
              }
            }
          }
          const nestedGroupPath = `${mg.pathKey}__op_${opt._id}__mg_${nestedMgId}`;
          const nestedInst = groupsByPathKey.get(nestedGroupPath);
          const nestedOptArr = Array.isArray(nestedInst?.modifierOptions)
            ? nestedInst.modifierOptions
            : Object.values(nestedInst?.modifierOptions || {});
          const nestedOpt = nestedOptArr.find(
            (o) => o != null && o._id === nestedOptId
          );
          const nestedOptPrice =
            nestedOpt?.singlePrice ?? nestedOpt?.price ?? 0;
          subAQ += nestedOpt?.actualQuantityWithMultiplier ?? 0;
          const base =
            subNames.length === 0 && subPrice === 0 && nestedOptPrice === 0
              ? opt
              : {
                  ...opt,
                  name: {
                    ...opt.name,
                    en: `${opt.name?.en || ''} ${subNames.join(' ')}`,
                  },
                  price:
                    (opt.singlePrice ?? opt.price ?? 0) +
                    subPrice +
                    nestedOptPrice,
                  singlePrice:
                    (opt.singlePrice ?? opt.price ?? 0) +
                    subPrice +
                    nestedOptPrice,
                };
          return { ...base, _groupedEffectiveAQ: subAQ };
        });
        _groupedEntries.push({ name, opts: enhancedParentOpts });
      }
      const _parentFreeMap = {};
      for (let eIdx = 0; eIdx < _groupedEntries.length; eIdx++) {
        for (let oIdx = 0; oIdx < _groupedEntries[eIdx].opts.length; oIdx++) {
          const opt = _groupedEntries[eIdx].opts[oIdx];
          const key = opt._id || `idx_${oIdx}`;
          if (!_parentFreeMap[key]) _parentFreeMap[key] = [];
          _parentFreeMap[key].push({
            eIdx,
            oIdx,
            aq: opt._groupedEffectiveAQ || 0,
          });
        }
      }
      for (const entries of Object.values(_parentFreeMap)) {
        if (entries.length <= 1) continue;
        const totalFree =
          _groupedEntries[entries[0].eIdx].opts[entries[0].oIdx].freeCount || 0;
        if (!totalFree) continue;
        const distributed = distributeMergedFreeCount(
          totalFree,
          entries.map((e) => ({ actualQuantityWithMultiplier: e.aq }))
        );
        entries.forEach((e, i) => {
          _groupedEntries[e.eIdx].opts[e.oIdx] = {
            ..._groupedEntries[e.eIdx].opts[e.oIdx],
            freeCount: distributed.perOption[i],
          };
        });
      }
      for (const entry of _groupedEntries) {
        result.push({
          ...mg,
          modifierOptions: {},
          isGroupedByNested: true,
          groupedNestedOptionName: entry.name,
          groupedParentOptions: entry.opts,
        });
      }
      for (const nestedMG of allNestedForGroupCheck) {
        if (nestedMG.groupAllParentsByThisOption !== true) {
          addMGRecursively(nestedMG);
        } else {
          processed.add(nestedMG.pathKey);
          for (const [pk] of groupsByPathKey) {
            if (pk.startsWith(nestedMG.pathKey + '__')) {
              processed.add(pk);
            }
          }
        }
      }
      return;
    }

    if (splitEntries.length === 0) {
      if (mergeParentNestedMGs.length > 0) {
        // Merge option(s) with nested children exist.
        // Determine which options are merge options so we can split
        // the MG into: merge segment → nested MGs → individual segment.
        const mergeOptIds = new Set();
        for (const opt of allOptions) {
          const optLayout = getEffectiveInOrderViewLayout(
            { inOrderViewLayout: opt.inOrderViewLayout },
            { inOrderViewLayout: mg.inOrderViewLayout }
          );
          const optMerges =
            opt.priceType === PRICE_TYPES.MULTIPLIER_APPLYING_TO_PARENT_PRICE &&
            !!optLayout;
          if (optMerges) {
            mergeOptIds.add(opt._id);
          }
        }

        const mergeOpts = allOptions.filter((o) => mergeOptIds.has(o._id));
        const individualOpts = allOptions.filter(
          (o) => !mergeOptIds.has(o._id)
        );

        if (individualOpts.length > 0 && mergeOpts.length > 0) {
          // Split: merge segment first (renders merged line only)
          // Use option objects directly (modifierOptions may be an array after SDK processing)
          const mergeModOpts = {};
          for (const opt of mergeOpts) {
            mergeModOpts[opt._id] = opt;
          }
          result.push({
            ...mg,
            modifierOptions: mergeModOpts,
          });

          // Then nested MGs for merge parent options
          for (const nestedList of mergeParentNestedMGs) {
            for (const nestedMG of nestedList) {
              addMGRecursively(nestedMG);
            }
          }

          // Then individual segment (renders individual options only)
          // Use option objects directly (modifierOptions may be an array after SDK processing)
          const indivModOpts = {};
          for (const opt of individualOpts) {
            indivModOpts[opt._id] = opt;
          }
          result.push({
            ...mg,
            modifierOptions: indivModOpts,
            isSegmentContinuation: true,
          });
        } else {
          // Only merge options (no individual) - push full MG then nested
          result.push(mg);
          for (const nestedList of mergeParentNestedMGs) {
            for (const nestedMG of nestedList) {
              addMGRecursively(nestedMG);
            }
          }
        }
      } else {
        // No merge parent nested MGs - push full MG as-is
        result.push(mg);
      }
      return;
    }

    // Split into segments around non-merge parent options so nested MGs
    // appear at the correct position among individual options
    const sortNestedMG = splitEntries
      .flatMap((e) => e.nestedMGs)
      .find((n) => n.sortParentOptionsByThisOptionsSorting === true);
    const resultBeforeSplits = result.length;

    let lastEnd = 0;
    let isFirst = true;

    for (const { index, nestedMGs } of splitEntries) {
      // Build a segment with options from lastEnd to index (exclusive)
      // Use option objects directly (modifierOptions may be an array after SDK processing)
      const segmentOptions = {};
      for (let j = lastEnd; j < index; j++) {
        segmentOptions[allOptions[j]._id] = allOptions[j];
      }

      if (Object.keys(segmentOptions).length > 0 || isFirst) {
        result.push({
          ...mg,
          modifierOptions: segmentOptions,
          isSegmentContinuation: !isFirst,
        });
        isFirst = false;
      }

      // Push the nested MGs for this non-merge parent option
      for (const nestedMG of nestedMGs) {
        addMGRecursively(nestedMG);
      }

      lastEnd = index + 1; // past the parent option
    }

    // Push remaining options after the last split point
    // Use option objects directly (modifierOptions may be an array after SDK processing)
    const remainingOptions = {};
    for (let j = lastEnd; j < allOptions.length; j++) {
      remainingOptions[allOptions[j]._id] = allOptions[j];
    }

    if (Object.keys(remainingOptions).length > 0) {
      result.push({
        ...mg,
        modifierOptions: remainingOptions,
        isSegmentContinuation: !isFirst,
      });
    } else if (isFirst) {
      // All options are parents - still need an entry to show the MG name
      result.push({
        ...mg,
        modifierOptions: {},
        isSegmentContinuation: false,
      });
    }

    // Push nested MGs for merge parent options (they stay in segments,
    // their nested MGs appear after all segments of this MG)
    for (const nestedList of mergeParentNestedMGs) {
      for (const nestedMG of nestedList) {
        addMGRecursively(nestedMG);
      }
    }

    if (sortNestedMG) {
      const sortMgId = sortNestedMG._id || '';
      let sortMgOptionIds = sortNestedMG.optionIds || [];
      const addedEntries = result.splice(resultBeforeSplits);
      if (sortMgOptionIds.length === 0) {
        const firstSortEntry = addedEntries.find((e) => e._id === sortMgId);
        if (firstSortEntry) {
          const mOpts = firstSortEntry.modifierOptions;
          sortMgOptionIds = Array.isArray(mOpts)
            ? mOpts
                .filter((o) => o != null)
                .map((o) => o._id)
                .filter(Boolean)
            : Object.keys(mOpts || {}).filter((k) => mOpts[k] != null);
        }
      }
      const mgPathPrefix = mg.pathKey;
      const parentMgId = mg._id;

      const headerEntries = [];
      const parentEntriesMap = new Map();
      const parentOrder = [];

      for (const entry of addedEntries) {
        if (entry._id === parentMgId) {
          headerEntries.push(entry);
          continue;
        }
        const afterMgPath = entry.pathKey.substring(mgPathPrefix.length);
        const parts = afterMgPath.split('__');
        const parentOptId = parts[1]?.replace('op_', '') || 'unknown';
        if (!parentEntriesMap.has(parentOptId)) {
          parentEntriesMap.set(parentOptId, []);
          parentOrder.push(parentOptId);
        }
        parentEntriesMap.get(parentOptId).push(entry);
      }

      const parentSubBlocks = new Map();
      for (const [parentOptId, pEntries] of parentEntriesMap) {
        const sortMgEntries = pEntries.filter((e) => e._id === sortMgId);

        if (sortMgEntries.length === 0 || sortMgOptionIds.length === 0) {
          parentSubBlocks.set(parentOptId, [pEntries]);
          continue;
        }

        const sortMgPathKey = sortMgEntries[0].pathKey;

        const combinedOpts = {};
        for (const sEntry of sortMgEntries) {
          const srcGroup = groupsByPathKey.get(sEntry.pathKey);
          const mOpts = srcGroup?.modifierOptions ?? sEntry.modifierOptions;
          if (Array.isArray(mOpts)) {
            for (const opt of mOpts) {
              if (opt && opt._id) combinedOpts[opt._id] = opt;
            }
          } else {
            Object.assign(combinedOpts, mOpts);
          }
        }

        const sortChildren = [];
        const otherEntries = [];
        for (const entry of pEntries) {
          if (entry._id === sortMgId) {
            continue;
          }
          if (entry.pathKey.startsWith(sortMgPathKey + '__op_')) {
            sortChildren.push(entry);
          } else {
            otherEntries.push(entry);
          }
        }

        const byOption = new Map();
        for (const entry of sortChildren) {
          const afterSortMg = entry.pathKey.substring(sortMgPathKey.length);
          const sParts = afterSortMg.split('__');
          const sortOptId = sParts[1]?.replace('op_', '') || 'unknown';
          if (!byOption.has(sortOptId)) {
            byOption.set(sortOptId, []);
          }
          byOption.get(sortOptId).push(entry);
        }

        const selectedOptIds = sortMgOptionIds.filter(
          (id) => combinedOpts[id] != null
        );
        const allSortOptIds = Object.keys(combinedOpts).filter(
          (k) => combinedOpts[k] != null
        );
        const effectiveOptIds = [
          ...selectedOptIds,
          ...allSortOptIds.filter((id) => !selectedOptIds.includes(id)),
        ];

        const blocks = [];
        for (const optId of effectiveOptIds) {
          const block = [];
          const optData = combinedOpts[optId];
          if (optData) {
            block.push({
              ...sortMgEntries[0],
              modifierOptions: { [optId]: optData },
              isSegmentContinuation: blocks.length > 0,
            });
          }
          const nestedEntries = byOption.get(optId) || [];
          block.push(...nestedEntries);
          if (block.length > 0) {
            blocks.push(block);
          }
        }

        if (otherEntries.length > 0) {
          if (blocks.length > 0) {
            blocks[blocks.length - 1].push(...otherEntries);
          } else {
            blocks.push(otherEntries);
          }
        }

        parentSubBlocks.set(parentOptId, blocks);
      }

      if (headerEntries.length > 0) {
        result.push(headerEntries[0]);
      }

      const maxBlocks = Math.max(
        ...Array.from(parentSubBlocks.values()).map((b) => b.length),
        0
      );
      for (let blockIdx = 0; blockIdx < maxBlocks; blockIdx++) {
        for (const pId of parentOrder) {
          const blocks = parentSubBlocks.get(pId) || [];
          if (blockIdx < blocks.length) {
            result.push(...blocks[blockIdx]);
          }
        }
      }

      for (let h = 1; h < headerEntries.length; h++) {
        result.push(headerEntries[h]);
      }
    }
  };

  // Level 0 sorted by product's modifierGroupIds
  const mgIds = productModifierGroupIds || [];
  const level0Groups = Array.from(groupsByPathKey.values())
    .filter((mg) => mg.level === 0)
    .sort((a, b) => {
      const indexA = mgIds.indexOf(a._id || '');
      const indexB = mgIds.indexOf(b._id || '');
      return (indexA === -1 ? 9999 : indexA) - (indexB === -1 ? 9999 : indexB);
    });

  for (const mg of level0Groups) {
    addMGRecursively(mg);
  }
  // Add any remaining
  for (const mg of groupsByPathKey.values()) {
    if (!processed.has(mg.pathKey)) {
      result.push(mg);
    }
  }

  return { data: result, parentOptionPathKeys };
};

// ---------------------------------------------------------------------------
// CurrentSelection – direct port from POS client CurrentSelectionOrders.tsx
// ---------------------------------------------------------------------------
const CurrentSelection = ({
  selectedCategory,
  selectedProduct,
  modifierGroups,
  count,
  menuMap,
}) => {
  if (!selectedProduct) return null;

  // Convert local state (object keyed by pathKey) to array with pathKey on each group
  const groups = useMemo(() => {
    return Object.entries(modifierGroups || {})
      .map(([pk, mg]) => ({ ...mg, pathKey: pk }))
      .filter((mg) => mg !== null);
  }, [modifierGroups]);

  const { data: modifierGroupData, parentOptionPathKeys } = useMemo(
    () =>
      buildModifierGroupData({
        groups,
        productModifierGroupIds: selectedProduct?.modifierGroupIds || [],
      }),
    [groups, selectedProduct]
  );

  // Calculate total price
  let modifierTotal = 0;
  Object.values(modifierGroups || {}).forEach((mg) => {
    Object.values(mg.modifierOptions || {}).forEach((opt) => {
      modifierTotal += (opt.price || 0) * (opt.count || 0);
    });
  });
  const singlePrice = (selectedProduct.price || 0) + modifierTotal;
  const totalPrice = singlePrice * count;

  return (
    <Box
      sx={{
        p: 1.5,
        mx: 1,
        mb: 1,
        bgcolor: POS_COLORS.YELLOW_OPACITY,
        borderRadius: 2,
        border: `1px dashed ${POS_COLORS.PRODUCT_BG}`,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: POS_COLORS.PRIMARY_OLD,
          fontWeight: 700,
          textTransform: 'uppercase',
        }}
      >
        Current Selection
      </Typography>

      {selectedCategory && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            color: POS_COLORS.ICONS,
            mt: 0.3,
          }}
        >
          {getTranslationValue(selectedCategory.name)}
        </Typography>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
        <Typography
          variant="body2"
          sx={{ fontWeight: 900, color: POS_COLORS.BLACK }}
        >
          {count > 1 && `${count}x `}
          {getTranslationValue(selectedProduct.name)}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 900,
            color: POS_COLORS.BLACK,
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
        >
          {getFloatPrice(
            selectedProduct.priceWithoutModifier ?? selectedProduct.price
          )}
        </Typography>
      </Box>

      {/* Modifier groups – rendered with shownParents deduplication (POS client) */}
      {(() => {
        const shownParents = new Set();
        return modifierGroupData.map((modifier, index) => {
          const pathParts = (modifier.pathKey || '').split('__');
          const parentPathKey =
            pathParts.length > 1 ? pathParts.slice(0, -1).join('__') : '';
          const parentKey = parentPathKey || '';
          const isFirstWithParent =
            parentKey === '' || !shownParents.has(parentKey);
          if (parentKey) {
            shownParents.add(parentKey);
          }

          return (
            <ModifierGroupItem
              key={`${modifier._id}-${index}`}
              modifier={modifier}
              level={modifier.level}
              parentOptionName={modifier.parentOptionName}
              parentOptionCount={modifier.parentOptionCount}
              parentOptionPrice={modifier.parentOptionPrice}
              parentOptionFreeCount={modifier.parentOptionFreeCount}
              parentOptionPriceType={modifier.parentOptionPriceType}
              parentModifierGroupHideNameOnOrder={
                modifier.parentModifierGroupHideNameOnOrder
              }
              parentModifierGroupCanSelectMultipleOptions={
                modifier.parentModifierGroupCanSelectMultipleOptions
              }
              parentOptionPathKeys={parentOptionPathKeys}
              modifierPathKey={modifier.pathKey}
              isFirstWithParent={isFirstWithParent}
              effectiveParentOptionName={modifier.effectiveParentOptionName}
              pendingMergeOptionNames={modifier.pendingMergeOptionNames}
              pendingMergeRightOptionNames={
                modifier.pendingMergeRightOptionNames
              }
              hideParentBecauseDescendantMerges={
                modifier.hideParentBecauseDescendantMerges
              }
              suppressMergedLine={modifier.suppressMergedLine}
              isGroupedByNested={modifier.isGroupedByNested}
              groupedNestedOptionName={modifier.groupedNestedOptionName}
              groupedParentOptions={modifier.groupedParentOptions}
            />
          );
        });
      })()}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// OrderItemRow – direct port from POS client OrderProduct/index.tsx
// SDK returns item.modifierGroup as a FLAT array. Hierarchy is in pathKeys.
// ---------------------------------------------------------------------------
const OrderItemRow = ({ item, onRemove }) => {
  // Resolve pathKeys from multiple sources (matching POS client pathKeyMap logic)
  const groups = useMemo(() => {
    const mgs = item.modifierGroup || [];
    const pathKeyMap = {};

    // Deep-copy helper: values may be frozen arrays from state,
    // so we must clone them before any mutation (push, splice, etc.).
    const copyPathKeys = (src) => {
      for (const [key, value] of Object.entries(src)) {
        pathKeyMap[key] = Array.isArray(value) ? [...value] : value;
      }
    };

    // From item metadata
    const itemMeta = item.metadata?.modifierPathKeys;
    if (itemMeta) copyPathKeys(itemMeta);

    // From groups directly
    const occurrenceCount = {};
    return mgs.map((mg, idx) => {
      let pathKey;
      if (mg.pathKey) {
        pathKey = mg.pathKey;
      } else {
        const groupId = mg._id || '';
        const mapValue = pathKeyMap[groupId];
        if (Array.isArray(mapValue)) {
          const occIdx = occurrenceCount[groupId] || 0;
          occurrenceCount[groupId] = occIdx + 1;
          pathKey = mapValue[occIdx] || groupId;
        } else {
          pathKey = mapValue || groupId;
        }
        if (!pathKey) {
          pathKey = `fallback_mg_${idx}`;
        }
      }
      return { ...mg, pathKey };
    });
  }, [item]);

  const { data: modifierGroupData, parentOptionPathKeys } = useMemo(
    () =>
      buildModifierGroupData({
        groups,
        productModifierGroupIds: item.modifierGroupIds || [],
      }),
    [groups, item]
  );

  return (
    <Box sx={{ px: 1.5, py: 0.8 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 900, color: POS_COLORS.BLACK }}
          >
            {item.count > 1 && `${item.count}x `}
            {getTranslationValue(item.name)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 900, color: POS_COLORS.BLACK }}
          >
            {getFloatPrice(item.singlePriceWithoutModifier ?? item.price)}
          </Typography>
          {onRemove && (
            <IconButton
              size="small"
              onClick={() => onRemove(item.uuid)}
              sx={{ p: 0.2, color: POS_COLORS.RED_COUNT }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Modifier groups – rendered with shownParents deduplication (POS client) */}
      {(() => {
        const shownParents = new Set();
        return modifierGroupData.map((modifier, index) => {
          const pathParts = (modifier.pathKey || '').split('__');
          const parentPathKey =
            pathParts.length > 1 ? pathParts.slice(0, -1).join('__') : '';
          const parentKey = parentPathKey || '';
          const isFirstWithParent =
            parentKey === '' || !shownParents.has(parentKey);
          if (parentKey) {
            shownParents.add(parentKey);
          }

          return (
            <ModifierGroupItem
              key={`${modifier._id}-${index}`}
              modifier={modifier}
              level={modifier.level}
              parentOptionName={modifier.parentOptionName}
              parentOptionCount={modifier.parentOptionCount}
              parentOptionPrice={modifier.parentOptionPrice}
              parentOptionFreeCount={modifier.parentOptionFreeCount}
              parentOptionPriceType={modifier.parentOptionPriceType}
              parentModifierGroupHideNameOnOrder={
                modifier.parentModifierGroupHideNameOnOrder
              }
              parentModifierGroupCanSelectMultipleOptions={
                modifier.parentModifierGroupCanSelectMultipleOptions
              }
              parentOptionPathKeys={parentOptionPathKeys}
              modifierPathKey={modifier.pathKey}
              isFirstWithParent={isFirstWithParent}
              effectiveParentOptionName={modifier.effectiveParentOptionName}
              pendingMergeOptionNames={modifier.pendingMergeOptionNames}
              pendingMergeRightOptionNames={
                modifier.pendingMergeRightOptionNames
              }
              hideParentBecauseDescendantMerges={
                modifier.hideParentBecauseDescendantMerges
              }
              suppressMergedLine={modifier.suppressMergedLine}
              isGroupedByNested={modifier.isGroupedByNested}
              groupedNestedOptionName={modifier.groupedNestedOptionName}
              groupedParentOptions={modifier.groupedParentOptions}
            />
          );
        });
      })()}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// OrderPanel
// ---------------------------------------------------------------------------
const OrderPanel = ({
  selectedCategory,
  selectedProduct,
  modifierGroups,
  count,
  orderData,
  menuMap,
  onRemoveItem,
  onClearOrder,
}) => {
  const items = orderData?.items || [];
  const charges = orderData?.charges || {};

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#fff',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 1.5,
          borderBottom: `1px solid ${POS_COLORS.DIVIDER}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Order Preview
        </Typography>
        {items.length > 0 && (
          <Button
            size="small"
            color="error"
            onClick={onClearOrder}
            sx={{ textTransform: 'none', fontSize: '0.75rem' }}
          >
            Clear All
          </Button>
        )}
      </Box>

      {/* Scrollable content */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {/* Current selection being built */}
        <CurrentSelection
          selectedCategory={selectedCategory}
          selectedProduct={selectedProduct}
          modifierGroups={modifierGroups}
          count={count}
          menuMap={menuMap}
        />

        {/* Already-added items */}
        {items.length > 0 && (
          <>
            <Divider sx={{ mx: 1 }} />
            <Box sx={{ py: 0.5 }}>
              {items.map((item) => (
                <React.Fragment key={item.uuid}>
                  <OrderItemRow item={item} onRemove={onRemoveItem} />
                  <Divider sx={{ mx: 1.5 }} />
                </React.Fragment>
              ))}
            </Box>
          </>
        )}

        {/* Empty state */}
        {!selectedProduct && items.length === 0 && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Select items from the menu to build an order
            </Typography>
          </Box>
        )}
      </Box>

      {/* Footer with subtotal */}
      {items.length > 0 && (
        <Box
          sx={{
            borderTop: `1px solid ${POS_COLORS.DIVIDER}`,
            p: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Subtotal
            </Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              ${(charges.subTotal || 0).toFixed(2)}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default OrderPanel;
